import { useUpdateJobMutation } from '@/common/infrastructure/gql/generated/schema';
import { JobFormData } from '../types/JobTypes';
import { mapFormDataToUpdateInput, mapFormDataToUpdateInputWithoutTimeFields } from '../mappers/JobMappers';
import { useJobCrewTemporaryStore } from '@/store/JobCrewTemporaryStore';
import { useJobExtraSystemsTemporaryStore } from '@/store/JobExtraSystemsTemporaryStore';
import { createJobMutationError } from '../types/JobMutationError';

export const useUpdateJob = () => {
  const [updateJob, { data, loading, error }] = useUpdateJobMutation();
  const clearCrewJob = useJobCrewTemporaryStore((state) => state.clearJob);
  const clearExtraSystemsJob = useJobExtraSystemsTemporaryStore((state) => state.clearJob);

  const handleUpdateJob = async (formData: JobFormData) => {
    try {
      const mutationInput = mapFormDataToUpdateInput(formData);
      const results = await updateJob({
        variables: { job: mutationInput },
        refetchQueries: ['listJobs', 'getJob', 'GetRaptorJobs', 'listAvailableCrewMembers'],
        awaitRefetchQueries: true
      });

      const mutationResult = results.data?.updateRaptorJob;
      if (mutationResult && !mutationResult.isSuccess) {
        const errorData = mutationResult.error;
        throw createJobMutationError(errorData || {}, 'Job update failed');
      }

      // Clear temporary crew members and extra systems from store after successful save
      if (formData.id) {
        clearCrewJob(formData.id);
        clearExtraSystemsJob(formData.id);
      }
    } catch (err) {
      console.error('Error updating job:', err);
      throw err;
    }
  };

  return { handleUpdateJob, data, loading, error };
};

/**
 * Hook for updating job without time fields (startTime, onJobTime, leaveTime, showUpTime).
 * Use this when updating only crew members or flags to avoid sending time information.
 */
export const useUpdateJobWithoutTimeFields = () => {
  const [updateJob, { data, loading, error }] = useUpdateJobMutation();
  const clearCrewJob = useJobCrewTemporaryStore((state) => state.clearJob);
  const clearExtraSystemsJob = useJobExtraSystemsTemporaryStore((state) => state.clearJob);

  const handleUpdateJobWithoutTimeFields = async (formData: JobFormData) => {
    try {
      const mutationInput = mapFormDataToUpdateInputWithoutTimeFields(formData);
      const results = await updateJob({
        variables: { job: mutationInput },
        refetchQueries: ['listJobs', 'getJob', 'GetRaptorJobs', 'listAvailableCrewMembers'],
        awaitRefetchQueries: true
      });

      const mutationResult = results.data?.updateRaptorJob;
      if (mutationResult && !mutationResult.isSuccess) {
        const errorData = mutationResult.error;
        throw createJobMutationError(errorData || {}, 'Job update failed');
      }

      // Clear temporary crew members and extra systems from store after successful save
      if (formData.id) {
        clearCrewJob(formData.id);
        clearExtraSystemsJob(formData.id);
      }
    } catch (err) {
      console.error('Error updating job:', err);
      throw err;
    }
  };

  return { handleUpdateJobWithoutTimeFields, data, loading, error };
};
